import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Sidebar "Supplies" landing page.
//   building_admin / staff → immediately redirected to their facility's supplies page
//   regional_manager       → list of facilities in their region (with expiring counts)
//   super_admin / executive_staff → list of all active facilities
export default function Supplies() {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [facilities, setFacilities] = useState(null);
  const [expiringByFacility, setExpiringByFacility] = useState({});

  // Single-facility users get bounced to their facility's supplies page.
  // This MUST be a hook (not an early return before other hooks) or React's
  // rules-of-hooks are violated and the component crashes to a blank page.
  const singleFacility = ['building_admin', 'staff'].includes(user?.role) && user?.facilityId;

  useEffect(() => {
    if (singleFacility) {
      navigate(`/facilities/${user.facilityId}/supplies`, { replace: true });
    }
  }, [singleFacility]);

  useEffect(() => {
    // Don't load the full list if we're about to redirect a single-facility user.
    if (singleFacility) return;

    // Load active+tourable facilities for the picker, scoped per role.
    // The backend's /admin/facilities returns all facilities for everyone, so
    // we filter on the frontend for regional_manager.
    Promise.all([
      API.get('/admin/facilities?tourable=true'),
      API.get('/supplies?expiring_within_days=30'),
    ]).then(([fRes, sRes]) => {
      let list = fRes.data;
      if (user?.role === 'regional_manager') {
        list = list.filter(f => f.region_id === user.regionId);
      }
      setFacilities(list);

      // Group expiring counts by facility for badges
      const counts = {};
      sRes.data.forEach(s => {
        counts[s.facility_id] = (counts[s.facility_id] || 0) + 1;
      });
      setExpiringByFacility(counts);
    }).catch(() => {
      setFacilities([]);
    });
  }, [singleFacility]);

  // While redirecting a single-facility user, render nothing (avoids a flash).
  if (singleFacility) return null;

  if (facilities === null) {
    return <div className="loading-screen"><div className="spinner"/></div>;
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Emergency Supplies</h2>
          <p>Select a facility to view its supply inventory</p>
        </div>
      </div>

      <div className="page-body">
        {facilities.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>No facilities available</h3>
            <p>You don't have supply access at any facility yet.</p>
          </div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:16}}>
            {facilities.map(f => {
              const expiring = expiringByFacility[f.id] || 0;
              return (
                <Link
                  key={f.id}
                  to={`/facilities/${f.id}/supplies`}
                  className="card"
                  style={{textDecoration:'none',display:'block',cursor:'pointer',transition:'transform 0.1s'}}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8}}>
                    <div style={{flex:1}}>
                      <h3 style={{fontSize:15,fontWeight:700,color:'var(--text-heading)',marginBottom:4}}>{f.name}</h3>
                      {f.region_name && <div style={{fontSize:12,color:'var(--text-secondary)'}}>{f.region_name}</div>}
                    </div>
                    {expiring > 0 && (
                      <span style={{
                        background:'var(--amber)', color:'white',
                        padding:'3px 9px', borderRadius:999,
                        fontSize:11, fontWeight:700, whiteSpace:'nowrap',
                      }}>{expiring} expiring</span>
                    )}
                  </div>
                  <div style={{marginTop:14,fontSize:13,color:'var(--blue)',fontWeight:600}}>
                    View supplies →
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
